import { computed, ref } from 'vue'

import { defineStore } from 'pinia'

import type { Auth0Client, User as Auth0User } from '@auth0/auth0-spa-js'
import { until } from '@vueuse/core'

import customers from '@/customers/customers.ts'
import type { Organization } from '@/types/Organization.ts'

import { getAuth0, parseJwt } from '../services/auth'
import type { User, UserMetadata } from '../types/User'

const maxReLoginTries = 7

interface Auth0UserMetadata {
  theme?: string
  language?: string
  timezone?: string
  dateFormat?: string
  coordinates?: string
  units?: string
}

function auth0UserToWfsafeUser(auth0User: Auth0User, token: string): User {
  const jwt = parseJwt<{ permissions?: string[] }>(token)
  const metadataClaim = auth0User[import.meta.env.VITE_AUTH0_NAMESPACE + '/user_metadata']
  const metadata: Auth0UserMetadata = metadataClaim
  const user: User = {
    id: auth0User.sub ?? '',
    email: auth0User.email,
    emailVerified: auth0User.email_verified,
    name: auth0User.name,
    firstName: auth0User.given_name,
    lastName: auth0User.family_name,
    roles: auth0User[import.meta.env.VITE_AUTH0_NAMESPACE + '/roles'] || [],
    attributes: metadataClaim || {},
    UserMetadata: {
      FamilyName: auth0User.family_name,
      GivenName: auth0User.given_name,
      Theme: metadata.theme,
      Language: metadata.language,
      Timezone: metadata.timezone,
      DateFormat: metadata.dateFormat,
      Coordinates: metadata.coordinates,
      Units: metadata.units
    },
    organization: auth0User.org_id,
    permissions: jwt?.permissions || []
  }

  return user
}

function setupOrganizationByAuth0User(user: User): Organization | null {
  return customers.find((c) => c.orgId === user.organization) ?? null
}

export default defineStore('auth', () => {
  const user = ref<null | User>(null)
  const org = ref<null | Organization>(null)
  const switchTenantNoop = ref(false)
  const initialised = ref<boolean>(false)
  let auth0: Auth0Client | undefined

  function setInitialised(val: boolean): void {
    initialised.value = val
  }

  const isAuthenticated = computed(() => {
    return user.value != null && user.value != undefined
  })

  const isInitialising = computed(() => !initialised.value)

  async function waitUntilInitialised() {
    await until(initialised).toBe(true)
  }

  function hasPermission(permission: string): boolean | undefined {
    return user.value?.permissions?.includes(permission)
  }

  async function init() {
    const url = new URL(window.location.href)
    console.debug('init auth')
    try {
      auth0 = await getAuth0()
      console.debug('got auth0 instance')
      // If Auth0 rejected login due to missing organization membership, skip token retrieval
      if (url.searchParams.get('error_description')?.includes('organization membership')) {
        console.debug('organization membership error, skipping token retrieval')
        return false
      }
      if (url.searchParams.get('code') && url.searchParams.get('state')) {
        console.debug('got auth0 redirect codes, handling redirect')
        try {
          await auth0.handleRedirectCallback()
        } catch (e) {
          // The code may have already been consumed (e.g. stale ?code in URL from a previous
          // navigation). Log a warning and continue — getTokenSilently() will still succeed
          // if a valid session exists in localStorage.
          console.warn('handleRedirectCallback failed (code may already be used):', e)
        }
        url.searchParams.delete('code')
        url.searchParams.delete('state')
        history.pushState({}, '', url)
        console.debug('redirect handled')
      } else if (url.searchParams.get('invitation')) {
        // User arrived via an Auth0 email invitation link. The invitation, organization, and
        // organization_name params were already baked into the Auth0 client in getAuth0().
        // Redirect immediately to the Auth0 login flow — no silent token attempt needed.
        console.debug('invitation param detected, redirecting to Auth0 login')
        await auth0.loginWithRedirect()
        return false
      }
      let token
      for (let attempt = 0; attempt < maxReLoginTries; attempt++) {
        try {
          token = await auth0.getTokenSilently()
        } catch {
          await new Promise((resolve) => setTimeout(resolve, 1000))
          continue
        }
        if (token) break
        else await new Promise((resolve) => setTimeout(resolve, 1000))
      }
      if (token) {
        // console.debug('got token, trying to get user')
        // if we have token, try to get the user a few times
        let u
        for (let attempt = 0; attempt < maxReLoginTries; attempt++) {
          u = await auth0.getUser()
          if (u) break
          await new Promise((resolve) => setTimeout(resolve, 1000))
        }
        // console.debug('user:', u)
        if (u) {
          user.value = auth0UserToWfsafeUser(u, token)
          org.value = setupOrganizationByAuth0User(user.value)
          const switchTenantFromOrgId = sessionStorage.getItem('wfsafe.switchTenant.fromOrgId')
          if (switchTenantFromOrgId !== null) {
            sessionStorage.removeItem('wfsafe.switchTenant.fromOrgId')
            switchTenantNoop.value = switchTenantFromOrgId === user.value.organization
          }
          return true
        }
      }
      return false
    } catch (error) {
      try {
        const url = new URL(window.location.href)
        const invitation = url.searchParams.get('invitation')
        if (invitation) await auth0?.loginWithRedirect()
      } catch {
        // outer catch reports the failure; nothing extra to do here
      }
      // login error or no auth0 query params
      console.debug('Error while initialising auth', error)
      return false
    }
  }

  function getAttributes() {
    return user?.value?.UserMetadata
  }

  function updateAttributes(attributes: UserMetadata) {
    let key: keyof UserMetadata
    for (key in attributes) {
      if (key && user.value && Object.prototype.hasOwnProperty.call(user?.value?.UserMetadata, key)) {
        user.value!.UserMetadata![key] = attributes[key] // TODO review this typing issue
      }
    }
  }

  async function login() {
    if (auth0) await auth0.loginWithRedirect()
  }

  async function logout() {
    if (auth0) {
      await auth0.logout({
        logoutParams: {
          returnTo: import.meta.env.VITE_AUTH0_REDIRECT_URI
        }
      })
    }
    user.value = null
  }

  async function switchTenant() {
    // Reuses the existing Auth0 SSO session instead of logging out first, so the user
    // isn't prompted for credentials again — Auth0 only asks them to pick an organization.
    if (auth0) {
      // Persisted across the full-page redirect so init() can detect a same-org no-op afterwards.
      sessionStorage.setItem('wfsafe.switchTenant.fromOrgId', org.value?.orgId ?? '')
      await auth0.loginWithRedirect()
    }
  }

  return {
    user,
    org,
    init,
    login,
    initialised,
    isInitialising,
    showUserAdminPanel: ref(false),
    setInitialised,
    waitUntilInitialised,
    isAuthenticated,
    hasPermission,
    getAttributes,
    logout,
    switchTenant,
    switchTenantNoop,
    updateAttributes
  }
})
